import React, { useMemo, useState } from 'react';
import {
  BellAlertIcon,
  ChevronDownIcon,
  PlusCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../../components/UI/Card';
import type { AreaCodeGapRow } from '../../../services/phoneNumberHealthService';
import { isValidUsAreaCode, sortGapsByUtilization, sortGapsForDisplay } from './healthDashboardUtils';
import UtilizationBar from './UtilizationBar';

const PAGE_SIZE = 8;
const UTILIZATION_ALERT_THRESHOLD = 0.8;

interface Props {
  gaps: AreaCodeGapRow[];
  periodLabel?: string;
  onPurchaseArea?: (areaCode: string) => void;
  onViewAll?: () => void;
  className?: string;
}

const InventoryGapsPanel: React.FC<Props> = ({ gaps, periodLabel = 'Today', onPurchaseArea, onViewAll, className = '' }) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const utilizationAlerts = useMemo(
    () =>
      sortGapsByUtilization(gaps).filter(
        (g) => isValidUsAreaCode(g.areaCode) && g.utilizationPercent >= UTILIZATION_ALERT_THRESHOLD
      ),
    [gaps]
  );

  const allGaps = useMemo(
    () => sortGapsForDisplay(gaps).filter((g) => g.missingNumbers > 0),
    [gaps]
  );

  const visibleGaps = allGaps.slice(0, visibleCount);
  const hasMore = visibleCount < allGaps.length;
  const remaining = allGaps.length - visibleCount;

  const loadMore = () => setVisibleCount((n) => Math.min(n + PAGE_SIZE, allGaps.length));

  return (
    <Card
      className={`border-amber-300/50 dark:border-amber-700/40 bg-gradient-to-br from-amber-50/80 to-orange-50/50 dark:from-amber-950/25 dark:to-orange-950/15 shadow-sm flex flex-col overflow-hidden h-full min-h-[22rem] ${className}`}
    >
      <CardContent className="p-4 flex flex-col h-full min-h-[22rem]">
        <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
          <div className="flex items-center gap-2">
            <BellAlertIcon className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0" />
            <p className="text-xs font-bold text-amber-900 dark:text-amber-200 uppercase tracking-wide">
              Alerts
            </p>
          </div>
          {(utilizationAlerts.length > 0 || allGaps.length > 0) && (
            <span className="text-[10px] font-semibold tabular-nums px-2 py-0.5 rounded-full bg-amber-200/60 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100">
              {utilizationAlerts.length + allGaps.length}
            </span>
          )}
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1 space-y-4"
          style={{ maxHeight: 'calc(22rem - 5rem)' }}
        >
          {utilizationAlerts.length > 0 && (
            <section>
              <div className="flex items-center gap-1.5 mb-2">
                <ChartBarIcon className="h-3.5 w-3.5 text-amber-800 dark:text-amber-300" />
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
                  High utilization ({periodLabel.toLowerCase()}, ≥80%)
                </p>
              </div>
              <ul className="space-y-2">
                {utilizationAlerts.slice(0, 6).map((g) => (
                  <li
                    key={`util-${g.areaCode}`}
                    className="rounded-lg bg-background/60 dark:bg-background/30 border border-amber-200/50 dark:border-amber-800/30 px-3 py-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold tabular-nums">
                          Area {g.areaCode}
                          <span className="text-muted-foreground font-normal ml-1.5 text-xs">{g.timezone}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {g.usedCapacityToday} calls · cap {g.dailyCapacity}
                        </p>
                      </div>
                      {g.suggestedPurchaseCount != null && onPurchaseArea && (
                        <button
                          type="button"
                          onClick={() => onPurchaseArea(g.areaCode)}
                          className="shrink-0 text-[10px] font-medium text-primary hover:underline"
                        >
                          Buy {g.suggestedPurchaseCount}
                        </button>
                      )}
                    </div>
                    <div className="mt-2">
                      <UtilizationBar
                        used={g.usedCapacityToday}
                        capacity={g.dailyCapacity}
                        percent={g.utilizationPercent}
                        showCounts={false}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {allGaps.length > 0 ? (
            <section>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200 mb-2">
                Missing inventory ({allGaps.length})
              </p>
              <p className="text-[10px] text-muted-foreground mb-2">
                Showing {visibleGaps.length} of {allGaps.length}
              </p>
              <ul className="space-y-2">
                {visibleGaps.map((g) => (
                  <li
                    key={g.areaCode}
                    className="flex items-start justify-between gap-2 rounded-lg bg-background/60 dark:bg-background/30 border border-amber-200/50 dark:border-amber-800/30 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground tabular-nums">
                        Area {g.areaCode}
                        <span className="text-muted-foreground font-normal ml-1.5 text-xs">{g.timezone}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {g.totalCallableLeads.toLocaleString()} leads · {g.activeNumbersOwned} owned ·{' '}
                        {g.usedCapacityToday} calls ({periodLabel.toLowerCase()})
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs font-bold text-red-600 dark:text-red-400 tabular-nums">
                        −{g.missingNumbers}
                      </span>
                      {onPurchaseArea && (
                        <button
                          type="button"
                          onClick={() => onPurchaseArea(g.areaCode)}
                          className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary hover:underline"
                        >
                          <PlusCircleIcon className="h-3 w-3" />
                          Buy
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : utilizationAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-6">
              <BellAlertIcon className="h-8 w-8 text-muted-foreground/30 mb-2" />
              <p className="text-sm font-medium text-muted-foreground">No alerts</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                Utilization under 80% and inventory gaps covered.
              </p>
            </div>
          ) : null}
        </div>

        {allGaps.length > 0 && (
          <div className="pt-3 mt-2 border-t border-amber-200/40 dark:border-amber-800/30 shrink-0 flex flex-col gap-2">
            {hasMore ? (
              <button
                type="button"
                onClick={loadMore}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-300/60 dark:border-amber-700/50 bg-background/50 hover:bg-background/80 px-3 py-2 text-xs font-medium text-amber-900 dark:text-amber-100 transition-colors"
              >
                <ChevronDownIcon className="h-3.5 w-3.5" />
                Load more ({Math.min(PAGE_SIZE, remaining)} of {remaining} left)
              </button>
            ) : (
              <p className="text-center text-[10px] text-muted-foreground">All gaps loaded</p>
            )}
            {onViewAll && allGaps.length > PAGE_SIZE && (
              <button
                type="button"
                onClick={onViewAll}
                className="text-[10px] font-medium text-primary hover:underline text-center"
              >
                View full table below ↓
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InventoryGapsPanel;
